/**
 * ADR-61 cut 4: the closed loop, as vocabulary and pure rules.
 *
 *   observe -> evaluate -> POLICY approve -> execute -> validate -> close
 *   observe regression -> roll back -> validate the rollback -> ROLLED_BACK -> INC
 *   UNCONFIRMED -> RECONCILING -> (desired running: validate | prior running: failed, no change | unknown: keep the lock, escalate)
 *
 * Three pins make an approval immutable: the surface (cut 3), the policy
 * version (cut 3) and, here, the evidence set: a digest of the exact gate
 * rows the decision relied on. A POLICY approval is granted only from that
 * immutable snapshot, and the executor refuses when the rows under it have
 * changed. No guessing anywhere: a criterion the platform cannot check is a
 * breach, an observation it cannot take is not a clean sample.
 */

import { type Classification, type Gate, type GateVerdict, type PolicyDefinition, approvalAllowed, CHANGE_ENGINE_IDENTITY } from './governance.js';
import { type ChangeCase } from './gates.js';

// ---------------------------------------------------------------------------
// The evidence pin
// ---------------------------------------------------------------------------

export interface EvidenceRow { readonly id: string; readonly gate: Gate; readonly verdict: GateVerdict; readonly evaluatedAt: string; readonly evaluatedBy: string }
export interface EvidencePin { readonly digest: string; readonly rows: readonly EvidenceRow[] }

function fnv1a64(s: string): string {
  let h = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  const mask = 0xffffffffffffffffn;
  for (let i = 0; i < s.length; i++) { h ^= BigInt(s.charCodeAt(i)); h = (h * prime) & mask; }
  return 'fnv1a64:' + h.toString(16).padStart(16, '0');
}

/**
 * The latest row per gate (rows newest first; pre-execution re-evaluations
 * are the executor's own and are excluded by the caller), pinned by id.
 * Order-insensitive: the digest is over the rows sorted by gate.
 */
export function evidencePin(rowsNewestFirst: readonly EvidenceRow[]): EvidencePin {
  const latest = new Map<Gate, EvidenceRow>();
  for (const r of rowsNewestFirst) if (!latest.has(r.gate)) latest.set(r.gate, r);
  const rows = [...latest.values()].sort((a, b) => a.gate.localeCompare(b.gate));
  const canonical = JSON.stringify(rows.map((r) => ({ gate: r.gate, id: r.id, verdict: r.verdict, evaluatedAt: r.evaluatedAt })));
  return { digest: fnv1a64(canonical), rows };
}

// ---------------------------------------------------------------------------
// POLICY approval: granted only from the immutable snapshot, under the constitution.
// ---------------------------------------------------------------------------

export interface PolicyApprovalInput {
  readonly classification: Classification;
  readonly policy: { readonly key: string; readonly version: number; readonly definition: PolicyDefinition } | null;
  readonly theCase: ChangeCase;
  readonly evidence: EvidencePin;
  readonly engineOperational: boolean;
}
export type PolicyApprovalDecision = { readonly grant: true; readonly because: string } | { readonly grant: false; readonly because: string };

export function policyApprovalDecision(input: PolicyApprovalInput): PolicyApprovalDecision {
  const allowed = approvalAllowed({ method: 'POLICY', by: CHANGE_ENGINE_IDENTITY }, input.classification, input.engineOperational);
  if (!allowed.allowed) return { grant: false, because: allowed.because ?? 'refused by the constitution' };
  if (!input.policy) return { grant: false, because: 'no policy in force for this change (no registered pattern); a human decides' };
  if (input.evidence.rows.length === 0) return { grant: false, because: 'no evidence on the record; evaluate the gates first' };
  if (input.theCase.verdict !== 'READY') return { grant: false, because: `the case is ${input.theCase.verdict}: ${input.theCase.statement}` };
  if (input.theCase.risk.within !== true) return { grant: false, because: `risk ${input.theCase.risk.score ?? 'unscored'} is not within the policy ceiling ${input.theCase.risk.ceiling ?? 'unset'}` };
  const missingRequired = input.policy.definition.requiredGates.filter((g) => !input.evidence.rows.some((r) => r.gate === g && r.verdict === 'PASS'));
  if (missingRequired.length > 0) return { grant: false, because: `required gate(s) without a PASS row in the pinned evidence: ${missingRequired.join(', ')}` };
  return { grant: true, because: `every gate ${input.policy.key} v${input.policy.version} requires is PASS in the pinned evidence (${input.evidence.digest}), risk ${input.theCase.risk.score} within the ceiling ${input.theCase.risk.ceiling}, no human line crossed` };
}

// ---------------------------------------------------------------------------
// Validation criteria: a small grammar the platform can check, and nothing it cannot.
// ---------------------------------------------------------------------------

export const VALIDATION_METRICS = [
  'ERROR_FINDINGS', 'WARNING_FINDINGS', 'TELEMETRY_OBSERVED', 'OPERATOR_CONNECTED',
  'RESYNCS_PER_MINUTE', 'RECONNECTS_PER_MINUTE', 'DECISION_P95_MS', 'BIDDERS_CONNECTED', 'CLIENT_RTT_P95_MS',
] as const;
export type ValidationMetric = (typeof VALIDATION_METRICS)[number];
export const CRITERION_OPS = ['==', '!=', '<', '<=', '>', '>='] as const;
export type CriterionOp = (typeof CRITERION_OPS)[number];
export interface Criterion { readonly metric: ValidationMetric; readonly op: CriterionOp; readonly value: number; readonly text: string }

/** `METRIC OP NUMBER`, e.g. `RESYNCS_PER_MINUTE <= 5`. Anything else is not a criterion. */
export function parseCriterion(text: string): { ok: true; criterion: Criterion } | { ok: false; text: string; why: string } {
  const m = /^\s*([A-Z0-9_]+)\s*(==|!=|<=|>=|<|>)\s*(-?\d+(?:\.\d+)?)\s*$/.exec(text);
  if (!m) return { ok: false, text, why: 'not of the form METRIC OP NUMBER' };
  const metric = m[1] as ValidationMetric;
  if (!VALIDATION_METRICS.includes(metric)) return { ok: false, text, why: `unknown metric ${m[1]}; the platform checks ${VALIDATION_METRICS.join(', ')}` };
  return { ok: true, criterion: { metric, op: m[2] as CriterionOp, value: Number(m[3]), text } };
}

/** One observation of the target ring, as the validator takes it each tick. null: the metric could not be read. */
export type ValidationSample = { readonly at: string } & { readonly [M in ValidationMetric]: number | null };

export interface SampleJudgement { readonly ok: boolean; readonly breaches: readonly { readonly criterion: string; readonly observed: number | null; readonly because: string }[] }

export function judgeSample(criteria: readonly string[], sample: ValidationSample): SampleJudgement {
  const breaches: { criterion: string; observed: number | null; because: string }[] = [];
  for (const text of criteria) {
    const p = parseCriterion(text);
    if (!p.ok) { breaches.push({ criterion: text, observed: null, because: `not machine-checkable: ${p.why}` }); continue; }
    const observed = sample[p.criterion.metric];
    if (observed === null) { breaches.push({ criterion: text, observed: null, because: `${p.criterion.metric} could not be observed this sample; an observation the platform cannot take is not a clean sample` }); continue; }
    const v = p.criterion.value;
    const holds = p.criterion.op === '==' ? observed === v : p.criterion.op === '!=' ? observed !== v : p.criterion.op === '<' ? observed < v : p.criterion.op === '<=' ? observed <= v : p.criterion.op === '>' ? observed > v : observed >= v;
    if (!holds) breaches.push({ criterion: text, observed, because: `${p.criterion.metric} was ${observed}; required ${p.criterion.op} ${v}` });
  }
  return { ok: breaches.length === 0, breaches };
}

// ---------------------------------------------------------------------------
// The post-execution state machine
// ---------------------------------------------------------------------------

export const EXECUTION_PHASES = [
  'EXECUTING', 'VALIDATING', 'VALIDATED', 'CLOSED',
  'REGRESSION_DETECTED', 'ROLLING_BACK', 'VALIDATING_ROLLBACK', 'ROLLED_BACK',
  'UNCONFIRMED', 'RECONCILING', 'ESCALATED', 'FAILED_NO_CHANGE',
  'FAILED', 'REFUSED',
] as const;
export type ExecutionPhase = (typeof EXECUTION_PHASES)[number];

/** Which phases may follow which. Everything else is a defect, refused by the engine and pinned by test. */
export const PHASE_TRANSITIONS: Readonly<Record<ExecutionPhase, readonly ExecutionPhase[]>> = {
  EXECUTING: ['VALIDATING', 'VALIDATING_ROLLBACK', 'UNCONFIRMED', 'FAILED', 'REFUSED'],
  VALIDATING: ['VALIDATED', 'REGRESSION_DETECTED'],
  VALIDATED: ['CLOSED'],
  CLOSED: [],
  REGRESSION_DETECTED: ['ROLLING_BACK'],
  ROLLING_BACK: ['VALIDATING_ROLLBACK', 'RECONCILING', 'ESCALATED'],
  VALIDATING_ROLLBACK: ['ROLLED_BACK', 'ESCALATED'],
  ROLLED_BACK: [],
  UNCONFIRMED: ['RECONCILING'],
  RECONCILING: ['VALIDATING', 'VALIDATING_ROLLBACK', 'FAILED_NO_CHANGE', 'ESCALATED'],
  ESCALATED: ['VALIDATING', 'VALIDATING_ROLLBACK', 'FAILED_NO_CHANGE'],
  FAILED_NO_CHANGE: [],
  FAILED: [],
  REFUSED: [],
};
/** Phases in which the ring stays held. */
export const HOLDING_PHASES: readonly ExecutionPhase[] = ['EXECUTING', 'VALIDATING', 'REGRESSION_DETECTED', 'ROLLING_BACK', 'VALIDATING_ROLLBACK', 'UNCONFIRMED', 'RECONCILING', 'ESCALATED'];
export function canAdvance(from: ExecutionPhase, to: ExecutionPhase): boolean { return PHASE_TRANSITIONS[from].includes(to); }

/** How many reconciliation ticks may fail to prove any of the three facts before the platform escalates. */
export const RECONCILE_ATTEMPTS_BEFORE_ESCALATION = 3;

/**
 * The reconciler's three facts. An observation is proof only when the
 * epoch moved (the ring did restart) AND the policy it reports is one of
 * the two values in play; anything else is "still cannot determine".
 */
export function reconcile(obs: { readonly epochBefore: number; readonly epochNow: number | null; readonly policyNow: string | null; readonly telemetry: 'observed' | 'stale' | 'absent' }, desired: string, prior: string): 'DESIRED_RUNNING' | 'PRIOR_RUNNING' | 'UNKNOWN' {
  if (obs.telemetry !== 'observed' || obs.epochNow === null || obs.policyNow === null) return 'UNKNOWN';
  if (obs.epochNow > obs.epochBefore && obs.policyNow === desired) return 'DESIRED_RUNNING';
  if (obs.policyNow === prior && obs.policyNow !== desired) return 'PRIOR_RUNNING';
  return 'UNKNOWN';
}
